import { useState, useEffect, useRef } from 'react';

interface AutoCompleteTextInputProps {
  suggestions: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  allowCustom?: boolean;
  separator?: string;
  disabled?: boolean;
}

export function AutoCompleteTextInput({
  suggestions,
  value,
  onChange,
  placeholder = 'Type to search...',
  label,
  allowCustom = true,
  separator = ', ',
  disabled = false
}: AutoCompleteTextInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse the value string into an array of selected items
  const selectedItems = value ? value.split(separator).map(s => s.trim()).filter(Boolean) : [];

  useEffect(() => {
    // Filter suggestions based on current input
    if (inputValue) {
      const filtered = suggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedItems.includes(suggestion)
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [inputValue, suggestions, selectedItems]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (suggestion: string) => {
    const newItems = [...selectedItems, suggestion];
    onChange(newItems.join(separator + ' '));
    setInputValue('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleRemove = (itemToRemove: string) => {
    const newItems = selectedItems.filter(item => item !== itemToRemove);
    onChange(newItems.join(separator + ' '));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Don't interfere with space key at all
    if (e.key === ' ') {
      // Just let it bubble through naturally
      return;
    }
    
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there are suggestions, select the first one
      if (filteredSuggestions.length > 0) {
        handleSelect(filteredSuggestions[0]);
      } 
      // If no suggestions but we have custom input and allowCustom is true, add it
      else if (allowCustom && inputValue.trim()) {
        const newItems = [...selectedItems, inputValue.trim()];
        onChange(newItems.join(separator + ' '));
        setInputValue('');
        setIsOpen(false);
      }
    }
    
    // Handle backspace to remove last item when input is empty
    if (e.key === 'Backspace' && inputValue === '' && selectedItems.length > 0) {
      const newItems = selectedItems.slice(0, -1);
      onChange(newItems.join(separator + ' '));
    }
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      
      {/* Selected items displayed as chips/tags */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedItems.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
            >
              {item}
              <button
                type="button"
                onClick={() => !disabled && handleRemove(item)}
                className={`ml-2 ${disabled ? 'cursor-not-allowed' : 'hover:text-blue-600'}`}
                disabled={disabled}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedItems.length > 0 ? 'Add more...' : placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        />

        {/* Dropdown suggestions */}
        {isOpen && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelect(suggestion)}
                className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              >
                <span className="text-sm text-gray-900">{suggestion}</span>
              </button>
            ))}
          </div>
        )}

        {/* Custom value hint */}
        {isOpen && allowCustom && inputValue && filteredSuggestions.length === 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3">
            <p className="text-sm text-gray-500">
              Press Enter to add "{inputValue}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
}